import React, { useState, useEffect, useRef } from 'react';
import { depositsAPI, eventsAPI } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Bitcoin, 
  Copy, 
  Check, 
  ArrowDownToLine,
  Clock,
  AlertCircle,
  DollarSign,
  ChevronRight,
  Upload,
  FileText,
  CheckCircle,
  Info,
  Wallet,
  Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { format } from 'date-fns';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3 }
};

export default function DepositsPage() {
  const { refreshUser } = useAuth();
  const [wallets, setWallets] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCrypto, setSelectedCrypto] = useState('BTC');
  const [amount, setAmount] = useState('');
  const [amountError, setAmountError] = useState('');
  const [copied, setCopied] = useState(false);
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [note, setNote] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [walletsRes, depositsRes] = await Promise.all([
        depositsAPI.getWallets(),
        depositsAPI.getAll()
      ]);
      setWallets(walletsRes.data);
      setDeposits(depositsRes.data.deposits);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const cryptoOptions = [
    { id: 'BTC', name: 'Bitcoin', icon: Bitcoin, color: 'text-yellow-500', bgColor: 'bg-yellow-500/10', borderColor: 'border-yellow-500/30', wallet: wallets?.btc },
    { id: 'ETH', name: 'Ethereum', icon: () => <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-xs font-bold text-white">E</div>, color: 'text-blue-400', bgColor: 'bg-blue-500/10', borderColor: 'border-blue-500/30', wallet: wallets?.eth },
    { id: 'USDT', name: 'Tether', icon: DollarSign, color: 'text-green-500', bgColor: 'bg-green-500/10', borderColor: 'border-green-500/30', wallet: wallets?.usdt }
  ];

  const selectedWallet = cryptoOptions.find(c => c.id === selectedCrypto);

  const validateAmount = (value) => {
    const num = parseFloat(value);
    if (!value || value === '') {
      setAmountError('');
      return false;
    }
    if (isNaN(num) || num <= 0) {
      setAmountError('Please enter a valid amount greater than 0');
      return false;
    }
    if (wallets?.min_deposit && num < wallets.min_deposit) {
      setAmountError(`Minimum deposit is $${wallets.min_deposit}`);
      return false;
    }
    if (wallets?.max_deposit && num > wallets.max_deposit) {
      setAmountError(`Maximum deposit is $${wallets.max_deposit.toLocaleString()}`);
      return false;
    }
    setAmountError('');
    return true;
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setAmount(value);
    validateAmount(value);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Wallet address copied!');
      setTimeout(() => setCopied(false), 2000);
      
      // Track the wallet copy event (for admin notifications)
      try {
        await eventsAPI.trackWalletCopy(selectedCrypto);
      } catch (trackError) {
        // Don't show error to user if tracking fails
        console.log('Event tracking skipped');
      }
    } catch (err) {
      toast.error('Failed to copy address');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setProofFile(file);
    }
  };

  const handleSubmitDeposit = async () => {
    if (!validateAmount(amount)) {
      toast.error('Please enter a valid deposit amount');
      return;
    }

    setSubmitting(true);
    try {
      // Create deposit request
      const response = await depositsAPI.create({
        amount: parseFloat(amount),
        crypto_type: selectedCrypto,
        note: note || null
      });

      // Upload proof if provided
      if (proofFile && response.data.deposit?.id) {
        const formData = new FormData();
        formData.append('file', proofFile);
        await depositsAPI.uploadProof(response.data.deposit.id, formData);
      }

      toast.success('Your deposit request has been submitted and is awaiting admin approval.');
      
      // Reset form
      setAmount('');
      setNote('');
      setProofFile(null);
      setShowSubmitForm(false);
      
      // Refresh data
      fetchData();
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit deposit request');
    } finally {
      setSubmitting(false);
    }
  };

  const pendingDeposits = deposits.filter(d => d.status === 'pending');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div 
      initial="initial"
      animate="animate"
      className="space-y-4 sm:space-y-6"
      data-testid="deposits-page"
    >
      {/* Header */}
      <motion.div variants={fadeInUp}>
        <h1 className="text-2xl sm:text-3xl font-outfit font-bold text-white">Deposit Funds</h1>
        <p className="text-white/60 mt-1 text-sm sm:text-base">Fund your account with cryptocurrency</p>
      </motion.div>

      {/* Instructions Card */}
      <motion.div variants={fadeInUp} className="glass-card p-4 sm:p-6 border-cyan-500/30">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center shrink-0">
            <Info className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold mb-1">How to Deposit</h3>
            <p className="text-white/50 text-sm">Follow these simple steps to fund your account</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-4 gap-3 sm:gap-4">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-white/5">
            <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center shrink-0 text-cyan-400 text-sm font-bold">1</div>
            <div>
              <p className="text-white text-sm font-medium">Enter Amount</p>
              <p className="text-white/50 text-xs mt-0.5">How much do you want to deposit?</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 rounded-lg bg-white/5">
            <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center shrink-0 text-cyan-400 text-sm font-bold">2</div>
            <div>
              <p className="text-white text-sm font-medium">Copy Address</p>
              <p className="text-white/50 text-xs mt-0.5">Copy the wallet address for your coin</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 rounded-lg bg-white/5">
            <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center shrink-0 text-cyan-400 text-sm font-bold">3</div>
            <div>
              <p className="text-white text-sm font-medium">Send Payment</p>
              <p className="text-white/50 text-xs mt-0.5">Send from your external wallet</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 rounded-lg bg-white/5">
            <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0 text-emerald-400 text-sm font-bold">4</div>
            <div>
              <p className="text-white text-sm font-medium">Confirm</p>
              <p className="text-white/50 text-xs mt-0.5">Submit your transaction details</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Main Deposit Card */}
      <motion.div variants={fadeInUp} className="glass-card p-4 sm:p-6">
        {/* Step 1: Amount Input */}
        <div className="mb-6">
          <Label className="text-white font-medium mb-3 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-cyan-500 flex items-center justify-center text-black text-sm font-bold">1</span>
            Enter Deposit Amount
          </Label>
          <div className="relative max-w-md">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 text-lg font-medium">$</span>
            <Input
              type="number"
              placeholder="0.00"
              value={amount}
              onChange={handleAmountChange}
              className="input-field pl-8 text-lg h-14 font-mono"
              data-testid="deposit-amount-input"
            />
          </div>
          {amountError && (
            <p className="text-hotpink-400 text-sm mt-2 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {amountError}
            </p>
          )}
          {!amountError && amount && parseFloat(amount) > 0 && (
            <p className="text-emerald-400 text-sm mt-2 flex items-center gap-1">
              <CheckCircle className="w-4 h-4" />
              You will deposit ${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
          )}
          <p className="text-white/40 text-xs mt-2">
            Min: ${wallets?.min_deposit || 10} • Max: ${wallets?.max_deposit?.toLocaleString() || '100,000'}
          </p>
        </div>

        {/* Step 2: Select Crypto */}
        <div className="mb-6">
          <Label className="text-white font-medium mb-3 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-cyan-500 flex items-center justify-center text-black text-sm font-bold">2</span>
            Select Cryptocurrency
          </Label>
          <div className="grid grid-cols-3 gap-3">
            {cryptoOptions.map((crypto) => {
              const isSelected = selectedCrypto === crypto.id;
              return (
                <button
                  key={crypto.id}
                  type="button"
                  onClick={() => setSelectedCrypto(crypto.id)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    isSelected
                      ? `${crypto.borderColor} ${crypto.bgColor} shadow-lg`
                      : 'border-white/10 hover:border-white/30 bg-white/5'
                  }`}
                  data-testid={`crypto-tab-${crypto.id}`}
                >
                  <div className="flex flex-col items-center gap-2">
                    {typeof crypto.icon === 'function' ? (
                      <crypto.icon />
                    ) : (
                      <crypto.icon className={`w-8 h-8 ${crypto.color}`} />
                    )}
                    <span className={`text-sm font-medium ${isSelected ? 'text-white' : 'text-white/70'}`}>
                      {crypto.id}
                    </span>
                    <span className="text-[10px] text-white/40">{crypto.name}</span>
                  </div>
                  {isSelected && (
                    <div className={`mt-2 w-full h-1 rounded-full ${crypto.bgColor.replace('/10', '/50')}`} />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Step 3: Wallet Address & QR Code */}
        <div className="mb-6">
          <Label className="text-white font-medium mb-3 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-cyan-500 flex items-center justify-center text-black text-sm font-bold">3</span>
            Send {selectedWallet?.name} to This Address
          </Label>
          
          <div className="flex flex-col lg:flex-row gap-6 p-4 sm:p-6 rounded-xl bg-white/5 border border-white/10">
            {/* QR Code */}
            <div className="flex flex-col items-center">
              <div className="bg-white p-4 rounded-xl">
                <QRCodeSVG 
                  value={selectedWallet?.wallet || ''} 
                  size={160}
                  bgColor="#ffffff"
                  fgColor="#020408"
                />
              </div>
              <p className="text-white/40 text-xs mt-2">Scan with your wallet app</p>
            </div>

            {/* Address & Copy */}
            <div className="flex-1 space-y-4">
              <div>
                <p className="text-white/60 text-sm mb-2">{selectedWallet?.name} Wallet Address</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <code className="flex-1 p-3 sm:p-4 bg-black/30 rounded-lg text-sm text-cyan-400 break-all font-mono border border-white/10">
                    {selectedWallet?.wallet}
                  </code>
                  <Button
                    onClick={() => copyToClipboard(selectedWallet?.wallet)}
                    className={`shrink-0 h-12 sm:h-auto px-4 ${copied ? 'bg-emerald-500 hover:bg-emerald-600' : 'btn-secondary'}`}
                    data-testid="copy-address-btn"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Address
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Warning */}
              <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-400/10 border border-yellow-400/30">
                <AlertCircle className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-yellow-400 font-medium">Important</p>
                  <p className="text-yellow-400/80">
                    Only send {selectedWallet?.name} ({selectedWallet?.id}) to this address. Sending other cryptocurrencies may result in permanent loss.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step 4: Confirm Button */}
        <div>
          <Label className="text-white font-medium mb-3 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center text-black text-sm font-bold">4</span>
            After Sending Payment
          </Label>
          
          {!showSubmitForm ? (
            <Button
              onClick={() => {
                if (!amount || parseFloat(amount) <= 0) {
                  toast.error('Please enter a deposit amount first');
                  return;
                }
                if (!validateAmount(amount)) {
                  return;
                }
                setShowSubmitForm(true);
              }}
              className="btn-primary w-full sm:w-auto text-base h-14 px-8"
              data-testid="sent-payment-btn"
            >
              <Send className="w-5 h-5 mr-2" />
              I Have Sent Payment
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          ) : (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-4 p-4 sm:p-6 rounded-xl bg-emerald-500/5 border border-emerald-500/30"
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-white font-semibold flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    Submit Your Deposit Details
                  </h4>
                  <button
                    onClick={() => setShowSubmitForm(false)}
                    className="text-white/50 hover:text-white text-sm"
                  >
                    Cancel
                  </button>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-2 gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                  <div>
                    <p className="text-white/50 text-xs">Amount</p>
                    <p className="text-white font-mono font-bold">
                      ${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs">Cryptocurrency</p>
                    <p className="text-white font-medium">{selectedWallet?.name} ({selectedWallet?.id})</p>
                  </div>
                </div>

                {/* Proof Upload */}
                <div className="space-y-2">
                  <Label className="text-white/80 text-sm">Proof of Payment (Optional)</Label>
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-white/20 hover:border-cyan-500/50 rounded-lg p-4 text-center cursor-pointer transition-colors"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    {proofFile ? (
                      <div className="flex items-center justify-center gap-2 text-emerald-400">
                        <FileText className="w-5 h-5" />
                        <span className="text-sm">{proofFile.name}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); setProofFile(null); }}
                          className="text-white/50 hover:text-white ml-2"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-white/50">
                        <Upload className="w-8 h-8" />
                        <span className="text-sm">Click to upload screenshot (max 5MB)</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Note */}
                <div className="space-y-2">
                  <Label className="text-white/80 text-sm">Additional Note (Optional)</Label>
                  <Textarea
                    placeholder="Any additional information..."
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="input-field min-h-[80px] resize-none"
                    data-testid="deposit-note-input"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  onClick={handleSubmitDeposit}
                  className="btn-primary w-full h-12"
                  disabled={submitting}
                  data-testid="submit-deposit-btn"
                >
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin mr-2" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Submit Deposit Request
                    </>
                  )}
                </Button>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </motion.div>

      {/* Pending Deposits Alert */}
      {pendingDeposits.length > 0 && (
        <motion.div variants={fadeInUp} className="glass-card p-4 border-yellow-500/30 bg-yellow-500/5">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-yellow-400 font-medium">Pending Deposits</p>
              <p className="text-yellow-400/70 text-sm">
                You have {pendingDeposits.length} deposit{pendingDeposits.length > 1 ? 's' : ''} awaiting admin approval
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Deposit History */}
      <motion.div variants={fadeInUp} className="glass-card p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-outfit font-semibold text-white mb-4 sm:mb-6">Deposit History</h2>
        
        {/* Mobile Cards View */}
        <div className="sm:hidden space-y-3">
          {deposits.map((deposit) => (
            <div key={deposit.id} className="p-4 bg-white/5 rounded-lg border border-white/10">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-mono font-bold">
                  ${deposit.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
                <span className={`status-badge ${
                  deposit.status === 'approved' ? 'status-badge-success' :
                  deposit.status === 'rejected' ? 'status-badge-danger' :
                  'status-badge-warning'
                }`}>
                  {deposit.status}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs text-white/50 mb-2">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {format(new Date(deposit.created_at), 'MMM dd, yyyy')}
                </span>
                <span>{deposit.crypto_type}</span>
              </div>
            </div>
          ))}
          {deposits.length === 0 && (
            <div className="text-center py-8">
              <Wallet className="w-12 h-12 text-white/20 mx-auto mb-3" />
              <p className="text-white/40 text-sm">No deposits yet</p>
              <p className="text-white/30 text-xs mt-1">Your deposit history will appear here</p>
            </div>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full" data-testid="deposits-table">
            <thead>
              <tr className="text-left text-white/50 text-sm border-b border-white/10">
                <th className="pb-3 font-medium">Date</th>
                <th className="pb-3 font-medium">Amount</th>
                <th className="pb-3 font-medium">Crypto</th>
                <th className="pb-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {deposits.map((deposit) => (
                <tr key={deposit.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="py-4">
                    <div className="flex items-center gap-2 text-white/70 text-sm">
                      <Clock className="w-4 h-4" />
                      {format(new Date(deposit.created_at), 'MMM dd, yyyy HH:mm')}
                    </div>
                  </td>
                  <td className="py-4">
                    <span className="font-mono font-bold text-white">
                      ${deposit.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </td>
                  <td className="py-4">
                    <span className="text-white/70">{deposit.crypto_type}</span>
                  </td>
                  <td className="py-4">
                    <span className={`status-badge ${
                      deposit.status === 'approved' ? 'status-badge-success' :
                      deposit.status === 'rejected' ? 'status-badge-danger' :
                      'status-badge-warning'
                    }`}>
                      {deposit.status}
                    </span>
                  </td>
                </tr>
              ))}
              {deposits.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-12 text-center">
                    <Wallet className="w-12 h-12 text-white/20 mx-auto mb-3" />
                    <p className="text-white/40">No deposits yet</p>
                    <p className="text-white/30 text-sm mt-1">Your deposit history will appear here</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </motion.div>
  );
}
